import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import {
  AdvancedSearchConfig,
  AdvancedSearchFormField,
  SearchOperator,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import { SaveIcon } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Button } from "../ui/button";
import { FormCol } from "../ui/form-col";
import { AdvancedSearchFormRow } from "./AdvancedSearchFormRow";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export type AdvancedSearchFormData = {
  fields: AdvancedSearchFormField[];
  searchMode: SearchOperator;
};

export const AdvancedSearchForm = ({
  config,
  onSaveAdvancedSearch,
}: {
  config: AdvancedSearchConfig;
  onSaveAdvancedSearch?: (data: AdvancedSearchFormData) => void;
}) => {
  const form = useFormContext<AdvancedSearchFormData>();

  const { t } = useTranslation();

  const { control, reset } = form;

  const fields = useFieldArray({
    name: "fields",
    control,
  });

  const { advancedSearchQuery: query, resetQuery } = useAdvancedSearch();

  useEffect(() => {
    reset({
      fields: query?.fields,
    });
  }, [reset, query]);

  function onAddRow() {
    fields.append({
      type: "unknown",
    });
  }
  function onDeleteRow(index: number) {
    fields.remove(index);
  }

  function onResetSearch() {
    reset({ fields: [{ type: "unknown" }] });
    resetQuery();
  }

  function onClickSaveQuery(data: AdvancedSearchFormData) {
    if (onSaveAdvancedSearch != undefined) onSaveAdvancedSearch(data);
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-semibold">{t("advancedSearchTitle")}</h2>
      <FormCol className="gap-3 items-start w-full">
        {fields.fields.map((field, index) => (
          <AdvancedSearchFormRow
            key={field.id}
            rowIndex={index}
            config={config}
            fieldRef={`fields.${index}`}
            onDeleteRow={onDeleteRow}
          />
        ))}
        <Button
          disabled={!form.formState.isValid}
          type="button"
          variant={"link"}
          onClick={onAddRow}
        >
          {t("advancedSearchAddCondition")}
        </Button>
      </FormCol>
      <div className="flex flex-row justify-between">
        <Button
          onClick={onResetSearch}
          type="button"
          variant={"link"}
          className="text-gray-600"
        >
          {t("dashBoardClearSearch")}
        </Button>
        <Button
          type="button"
          disabled={!form.formState.isValid}
          onClick={form.handleSubmit(onClickSaveQuery)}
        >
          <SaveIcon />
          {t("advancedSearchSave")}
        </Button>
      </div>
    </div>
  );
};
